import { useTranslation } from "@redotech/redo-i18n";
import { hasCompletedDropoffQrCodes } from "@redotech/redo-model/dropoff/utils";
import {
  getResource,
  Resource,
} from "@redotech/redo-model/localization/resource";
import {
  ErrorTag,
  getCommercialInvoiceUrl,
  PickupLocation,
  ReturnDropoff,
  ReturnWarning,
  UpsellBillingTrigger,
} from "@redotech/redo-model/return";
import { Trackable, TrackableType } from "@redotech/redo-model/trackable";
import { ReturnStatus } from "@redotech/redo-return-core-common/return-status";
import { CustomerPortalAppSettings } from "../../settings";
import {
  isInternationalReturn,
  localizeCustomerPortalFlowText,
} from "../../util";
import { getDropoffProviderCopy } from "../../utils/dropoff-provider-copy";

export enum StepTypeEnum {
  NONE = "none",
  EXCHANGE_ORDER = "exchange_order",
  MANUAL_REVIEW = "manual_review",
  GREEN_RETURN = "green_return",
  IN_TRANSIT = "in_transit",
  DELIVERED = "delivered",
  PROCESSED = "processed",
  WARNING = "warning",
}

export enum StepIdentifierEnum {
  NONE = "none",
  MANUAL_REVIEW = "manual-review",
  EXCHANGE_ORDER = "exchange-order",
  IN_STORE_PRINT_PACKING_SLIP = "in-store-print-packing-slip",
  IN_STORE_RETURN = "in-store-return",
  IN_STORE_PROCESSED = "in-store-processed",
  HAPPY_RETURNS_DROP_OFF = "happy-returns-drop-off",
  HAPPY_RETURNS_PROCESSED = "happy-returns-processed",
  DROPOFF = "dropoff",
  SHIPMENT_TRANSIT = "shipment-transit",
  PICKUP_PRINT_LABEL = "pickup-print-label",
  PICKUP_ATTACH_LABEL = "pickup-attach-label",
  PICKUP_MANAGE = "pickup-manage",
  DROP_OFF_PACKAGE = "drop-off-package",
  PROCESSED = "processed",
  PROCESSED_REPAIR = "processed-repair",
  SHIP_BACK_REPAIR = "ship-back-repair",
  GREEN_RETURN_INSTRUCTIONS = "green-return-instructions",
  GREEN_RETURN_PROCESSED = "green-return-processed",
  WARNING = "warning",
}

export type StepType =
  | ({ type: StepIdentifierEnum.NONE; stepType: StepTypeEnum.NONE } & Step)
  | ({
      type: StepIdentifierEnum.MANUAL_REVIEW;
      stepType: StepTypeEnum.MANUAL_REVIEW;
    } & Step)
  | ({
      type: StepIdentifierEnum.EXCHANGE_ORDER;
      stepType: StepTypeEnum.EXCHANGE_ORDER;
    } & Step)
  | ({
      type: StepIdentifierEnum.IN_STORE_PRINT_PACKING_SLIP;
      stepType: StepTypeEnum.IN_TRANSIT;
      packingSlipLink?: string;
    } & Step)
  | ({
      type: StepIdentifierEnum.IN_STORE_RETURN;
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.IN_STORE_PROCESSED;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.HAPPY_RETURNS_DROP_OFF;
      happyReturnsData: any;
      zipCode: string | undefined | null;
      stepType: StepTypeEnum.IN_TRANSIT;
      //todo
    } & Step)
  | ({
      type: StepIdentifierEnum.HAPPY_RETURNS_PROCESSED;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.DROPOFF;
      dropoffs: ReturnDropoff;
      customerCountry: string;
      stepType: StepTypeEnum.IN_TRANSIT;
      modalTitle?: string;
      modalSubtitle?: string;
    } & Step)
  | ({
      type: StepIdentifierEnum.SHIPMENT_TRANSIT;
      shipmentInfo?: {
        carrier: string | undefined;
        commercialInvoice: string | undefined | null;
        trackable: Trackable & { type: TrackableType.RETURN };
        qrCodeOnly?: boolean;
      };
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.PICKUP_PRINT_LABEL;
      shipmentInfo?: {
        carrier: string | undefined;
        commercialInvoice: string | undefined | null;
        trackable: Trackable & { type: TrackableType.RETURN };
        qrCodeOnly?: boolean;
      };
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.PICKUP_ATTACH_LABEL;
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.PICKUP_MANAGE;
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.DROP_OFF_PACKAGE;
      trackable: Trackable & { type: TrackableType.RETURN };
      stepType: StepTypeEnum.IN_TRANSIT;
    } & Step)
  | ({
      type: StepIdentifierEnum.PROCESSED;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.PROCESSED_REPAIR;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.SHIP_BACK_REPAIR;
      trackable: Trackable;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.GREEN_RETURN_INSTRUCTIONS;
      stepType: StepTypeEnum.GREEN_RETURN;
    } & Step)
  | ({
      type: StepIdentifierEnum.GREEN_RETURN_PROCESSED;
      stepType: StepTypeEnum.PROCESSED;
    } & Step)
  | ({
      type: StepIdentifierEnum.WARNING;
      stepType: StepTypeEnum.WARNING;
    } & Step);

export interface Step {
  stepType: StepTypeEnum;
  title: string;
  subtitle: string;
  complete: boolean;
}
export interface UseStepsResponse {
  steps: StepType[] | undefined;
  stepResource: Resource | undefined;
}

function isLostOrStolenClaim(
  trackable: Trackable & { type: TrackableType.RETURN },
): boolean {
  if (trackable.returnType !== "claim") {
    return false;
  }
  return trackable.returnProducts.every((product) => !product.damaged);
}

export function useSteps({
  settings,
  trackable,
  hasUnresolvedPickupWarnings,
  upsellBillingTrigger,
}: {
  settings: CustomerPortalAppSettings | undefined;
  trackable: Trackable | undefined;
  hasUnresolvedPickupWarnings?: boolean;
  upsellBillingTrigger?: UpsellBillingTrigger;
}): UseStepsResponse {
  const { i18n, t } = useTranslation();
  //Someone refactor this, I don't have time

  const steps: StepType[] = [];
  if (trackable?.type !== TrackableType.RETURN || !settings) {
    return { steps: undefined, stepResource: undefined };
  }
  const isRepair = trackable.returnProducts.some(
    (product: any) => product.strategy === "repair",
  );

  const isInternational = isInternationalReturn(
    trackable.originalOrder.shopify.shippingAddress?.country,
    undefined,
    settings,
  );

  const isBuyYourOwnLabel =
    settings.returns?.buyYourOwnLabel &&
    (isInternational || settings.returns?.byolDomestically);

  const hasPickup =
    trackable.pickup && trackable.pickup.status !== "pending_refund";

  const showQRCodeMessage =
    trackable.type === TrackableType.RETURN && !!trackable.form_label;
  const isQRCodeOnly =
    trackable.type === TrackableType.RETURN &&
    !!trackable.form_label &&
    !trackable.postage_label;
  const carrier = trackable?.fulfillments[0]?.trackers[0]?.carrier;

  const copyResource = getResource({
    variant: hasPickup ? "pickup" : trackable.returnType,
    overrides: settings.resourceOverride,
    // This is an example of how we could get the language region based on the country code
    // language: getLanguageRegion({
    //   countryCode: returnData.shipping_address?.country_code,
    // }),
  });

  if (trackable?.markedForManualReview && !settings.hideManualReviewStep) {
    steps.push({
      type: StepIdentifierEnum.MANUAL_REVIEW,
      title: localizeCustomerPortalFlowText(
        copyResource.STATUS_MANUAL_REVIEW_TITLE,
      ),
      subtitle: trackable.returnProducts.some(
        (product) => !product.green_return,
      )
        ? localizeCustomerPortalFlowText(
            copyResource.STATUS_MANUAL_REVIEW_SUBTITLE,
          )
        : "",
      complete: false,
      stepType: StepTypeEnum.MANUAL_REVIEW,
    });
  }

  if (
    trackable.draftOrderURL &&
    upsellBillingTrigger !== UpsellBillingTrigger.RETURN_CREATED
  ) {
    let subtitle = "";
    if (settings.exchanges.createLabelOnDraftOrderComplete) {
      subtitle = t("You'll receive your label after you complete your order");
    }
    steps.push({
      type: StepIdentifierEnum.EXCHANGE_ORDER,
      title: t("Pay for exchange order"),
      subtitle,
      complete: false,
      stepType: StepTypeEnum.EXCHANGE_ORDER,
    });
  }
  // If there are no label warnings
  if (
    !trackable.warnings?.some(
      (warning: ReturnWarning) => warning.type === ErrorTag.LABEL,
    )
  ) {
    if (trackable.inStoreReturn) {
      const hasPackingSlip =
        settings.inStoreDropOffPackingSlipEnabled && trackable.packingSlipLink;
      if (hasPackingSlip) {
        steps.push({
          type: StepIdentifierEnum.IN_STORE_PRINT_PACKING_SLIP,
          title: t("Print off return authorization"),
          subtitle: t("Click the button to print the return authorization"),
          complete: false,
          stepType: StepTypeEnum.IN_TRANSIT,
          packingSlipLink: trackable.packingSlipLink,
        });
      }
      steps.push({
        type: StepIdentifierEnum.IN_STORE_RETURN,
        title: t("Return in-store"),
        subtitle: t("You can bring your items to the store"),
        complete: false,
        stepType: StepTypeEnum.IN_TRANSIT,
      });
      steps.push({
        type: StepIdentifierEnum.IN_STORE_PROCESSED,
        title: t("We process the return"),
        subtitle: "",
        complete: false,
        stepType: StepTypeEnum.PROCESSED,
      });
    } else if (trackable.happyReturnsData) {
      steps.push({
        type: StepIdentifierEnum.HAPPY_RETURNS_DROP_OFF,
        title: t("Drop off your items"),
        subtitle: t(
          "You can drop off your items at any Happy Returns® location",
        ),
        happyReturnsData: trackable.happyReturnsData,
        zipCode: trackable.shipping_address?.zip,
        stepType: StepTypeEnum.IN_TRANSIT,
        complete: false,
      });
      steps.push({
        type: StepIdentifierEnum.HAPPY_RETURNS_PROCESSED,
        title: localizeCustomerPortalFlowText(
          copyResource.STATUS_PROCESSED_TITLE,
        ),
        subtitle: localizeCustomerPortalFlowText(
          copyResource.STATUS_PROCESSED_SUBTITLE,
        ),
        complete: false,
        stepType: StepTypeEnum.PROCESSED,
      });
    } else if (hasCompletedDropoffQrCodes(trackable)) {
      const firstProvider = trackable.dropoffs?.qrCodes[0]?.provider;
      const modalCopy = firstProvider
        ? getDropoffProviderCopy(firstProvider, copyResource)
        : undefined;
      steps.push({
        type: StepIdentifierEnum.DROPOFF,
        title: t("Drop off your items (no packaging or printing required)"),
        subtitle: t(
          "Drop off your items at a nearby location and show your QR code",
        ),
        dropoffs: trackable.dropoffs!,
        customerCountry: trackable.shipping_address?.country ?? "US",
        stepType: StepTypeEnum.IN_TRANSIT,
        complete: false,
        ...(modalCopy && {
          modalTitle: modalCopy.title,
          modalSubtitle: modalCopy.subtitle,
        }),
      });
      steps.push({
        type: StepIdentifierEnum.PROCESSED,
        title: localizeCustomerPortalFlowText(
          copyResource.STATUS_PROCESSED_TITLE,
        ),
        subtitle: localizeCustomerPortalFlowText(
          copyResource.STATUS_PROCESSED_SUBTITLE,
        ),
        complete: false,
        stepType: StepTypeEnum.PROCESSED,
      });
    } else if (
      trackable.returnProducts.some((product) => !product.green_return)
    ) {
      const commercialInvoice = getCommercialInvoiceUrl(
        trackable.shipment,
      )?.url;
      const singleShipment = !(
        trackable.shipments && trackable.shipments.length > 1
      );

      steps.push({
        type: StepIdentifierEnum.SHIPMENT_TRANSIT,
        title: localizeCustomerPortalFlowText(
          copyResource.STATUS_IN_TRANSIT_TITLE,
        ),
        subtitle: singleShipment
          ? isQRCodeOnly
            ? localizeCustomerPortalFlowText(
                copyResource.STATUS_IN_TRANSIT_QR_CODE_ONLY,
              )
            : localizeCustomerPortalFlowText(
                copyResource.STATUS_IN_TRANSIT_SUBTITLE,
              ) +
              (showQRCodeMessage
                ? " " +
                  localizeCustomerPortalFlowText(
                    copyResource.STATUS_IN_TRANSIT_QR_CODE,
                  )
                : "")
          : localizeCustomerPortalFlowText(
              copyResource.STATUS_IN_TRANSIT_MULTI_SHIPMENT_SUBTITLE,
            ),
        stepType: StepTypeEnum.IN_TRANSIT,
        shipmentInfo:
          !!trackable.shipments && !hasPickup
            ? {
                carrier,
                commercialInvoice,
                trackable,
                qrCodeOnly: !!settings.qrCodeOnlyShipping,
              }
            : undefined,

        complete: false,
      });
      if (hasPickup) {
        steps.push({
          type: StepIdentifierEnum.PICKUP_PRINT_LABEL,
          title: t("Print your shipping label"),
          subtitle: t("Click the button to print your shipping label"),
          shipmentInfo:
            !!trackable.shipment && singleShipment
              ? {
                  carrier,
                  commercialInvoice,
                  trackable,
                  qrCodeOnly: !!settings.qrCodeOnlyShipping,
                }
              : undefined,

          complete: false,
          stepType: StepTypeEnum.IN_TRANSIT,
        });
        steps.push({
          type: StepIdentifierEnum.PICKUP_ATTACH_LABEL,
          title: t("Attach shipping label"),
          subtitle: t(
            "Place label on outside of the package and make sure barcode is visible",
          ),
          complete: false,
          stepType: StepTypeEnum.IN_TRANSIT,
        });

        // SINCE "hasPickup" is true, we know that trackable.pickup is not undefined
        // but typescript doesn't know that, so we need to use the nullish coalescing operator
        // ideally it should never fallback to PickupLocation.OTHER, but just in case
        const pickupLocationLabel =
          trackable.pickup?.pickupLocation?.packageLocation ===
          PickupLocation.FRONT_DOOR
            ? t("front door")
            : trackable.pickup?.pickupLocation?.packageLocation ===
                PickupLocation.BACK_DOOR
              ? t("back door")
              : trackable.pickup?.pickupLocation?.packageLocation ===
                  PickupLocation.SIDE_DOOR
                ? t("side door")
                : t("specified location");

        const pickupDate = Temporal.PlainDate.from(
          trackable.pickup?.pickupDate ?? new Date().toISOString(),
        );

        // Show different messaging when pickup has failed vs scheduled successfully
        const pickupTitle = hasUnresolvedPickupWarnings
          ? t("Schedule package pickup")
          : t("Package pickup on {{pickupDate}}", {
              pickupDate: pickupDate.toLocaleString(i18n.language, {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: "numeric",
              }),
            });
        const pickupSubtitle = hasUnresolvedPickupWarnings
          ? t(
              "Your pickup couldn't be scheduled. Use the options above to try again or cancel.",
            )
          : t(
              "Leave your package on your {{pickupLocation}} and it will be picked up for you.",
              { pickupLocation: pickupLocationLabel },
            );

        steps.push({
          type: StepIdentifierEnum.PICKUP_MANAGE,
          title: pickupTitle,
          subtitle: pickupSubtitle,
          stepType: StepTypeEnum.IN_TRANSIT,
          complete: false,
        });
      } else {
        steps.push({
          type: StepIdentifierEnum.DROP_OFF_PACKAGE,
          title: carrier
            ? `${localizeCustomerPortalFlowText(
                copyResource.DROP_OFF_PACKAGE_TEXT,
              )} ${t("at")} ${carrier}`
            : localizeCustomerPortalFlowText(
                copyResource.DROP_OFF_PACKAGE_TEXT,
              ),
          subtitle: carrier
            ? `${localizeCustomerPortalFlowText(
                copyResource.DROP_OFF_PACKAGE_SUBTEXT,
              )} ${carrier} ${t("location")}`
            : "",
          complete: false,
          trackable,
          stepType: StepTypeEnum.IN_TRANSIT,
        });
      }
      steps.push({
        type: StepIdentifierEnum.PROCESSED,
        title: localizeCustomerPortalFlowText(
          copyResource.STATUS_PROCESSED_TITLE,
        ),
        subtitle: localizeCustomerPortalFlowText(
          copyResource.STATUS_PROCESSED_SUBTITLE,
        ),
        complete: false,
        stepType: StepTypeEnum.PROCESSED,
      });
      if (isRepair) {
        steps.push({
          type: StepIdentifierEnum.PROCESSED_REPAIR,
          title: localizeCustomerPortalFlowText(
            copyResource.STATUS_PROCESSED_REPAIR_TITLE,
          ),
          subtitle: localizeCustomerPortalFlowText(
            copyResource.STATUS_PROCESSED_REPAIR_SUBTITLE,
          ),
          complete: false,
          stepType: StepTypeEnum.PROCESSED,
        });
        steps.push({
          type: StepIdentifierEnum.SHIP_BACK_REPAIR,
          title: t("We ship the repaired item"),
          subtitle: "",
          complete: false,
          trackable,
          stepType: StepTypeEnum.PROCESSED,
        });
      }
    } else {
      const lostOrStolen = isLostOrStolenClaim(trackable);
      steps.push({
        type: StepIdentifierEnum.GREEN_RETURN_INSTRUCTIONS,
        title: lostOrStolen
          ? t("Nothing to return")
          : settings.theme.green_return_confirmation_text ||
            (isBuyYourOwnLabel
              ? t(
                  "Please visit a local carrier to purchase a label and get your items shipped",
                )
              : t("Keep your items")),
        subtitle: lostOrStolen
          ? t(
              "Since you never received your item, no action is needed on your end.",
            )
          : settings.theme.green_return_confirmation_description ||
            (isBuyYourOwnLabel
              ? `Our address is: \n${trackable.merchant_address?.name}\n${trackable.merchant_address?.address1} ${trackable.merchant_address?.address2}\n${trackable.merchant_address?.city}, ${trackable.merchant_address?.province} ${trackable.merchant_address?.zip}\n${trackable.merchant_address?.country}`
              : t("You do not need to send your items back.")),
        complete: false,
        stepType: StepTypeEnum.GREEN_RETURN,
      });
      steps.push({
        type: StepIdentifierEnum.GREEN_RETURN_PROCESSED,
        title: localizeCustomerPortalFlowText(
          copyResource.GREEN_RETURN_PROCESS_STEP_TEXT,
        ),
        subtitle: "",
        complete: false,
        stepType: StepTypeEnum.PROCESSED,
      });
    }
  } else {
    // If there are label warnings, we need to let them know that we are working on it
    steps.push({
      type: StepIdentifierEnum.WARNING,
      title: t("We're working on it"),
      subtitle: t("We will resolve any issues and email you with our solution"),
      complete: false,
      stepType: StepTypeEnum.WARNING,
    });
  }

  checkStepsCompletion(trackable, steps);
  return { steps, stepResource: copyResource };
}

const checkStepsCompletion = (
  trackable: Trackable & { type: TrackableType.RETURN },
  steps: Step[],
) => {
  // Go through steps array backwards. If the step is complete,
  // mark the rest of the steps as complete.
  let isComplete = false;
  for (let i = steps.length - 1; i >= 0; i--) {
    const step = steps[i];
    if (!step) {
      continue;
    }
    if (isComplete) {
      step.complete = true;
      continue;
    }
    switch (step.stepType) {
      case StepTypeEnum.PROCESSED:
        step.complete = trackable.status === ReturnStatus.COMPLETE;
        break;
      case StepTypeEnum.DELIVERED:
        step.complete = [
          ReturnStatus.COMPLETE,
          ReturnStatus.DELIVERED,
        ].includes(trackable.status);
        break;
      case StepTypeEnum.IN_TRANSIT:
        step.complete = [
          ReturnStatus.COMPLETE,
          ReturnStatus.DELIVERED,
          ReturnStatus.IN_TRANSIT,
        ].includes(trackable.status);
        break;
      case StepTypeEnum.EXCHANGE_ORDER:
        step.complete = trackable.exchangeOrder?.length > 0;
        break;
      case StepTypeEnum.MANUAL_REVIEW:
      case StepTypeEnum.GREEN_RETURN:
        step.complete = trackable.status !== ReturnStatus.NEEDS_REVIEW;
        break;
      case StepTypeEnum.NONE:
        continue;
    }
    isComplete = step.complete;
  }
};
